/**
 * Canonical currency input + display utilities for the ThreadFlow frontend.
 *
 * Per Jake's directive (2026-05-18): "Every currency field in this app
 * should use the same shared/common code for input and display."
 *
 * Two display formatters and one input parser:
 *   - parseCurrencyInput(raw)    → Number | null   (input → model)
 *   - formatCurrency(value)      → "$1,234.56"     (display, with $ sign)
 *   - formatCurrencyNumber(value)→ "1,234.56"      (display, no $ sign)
 *
 * Multi-currency note: the editable portion of a currency control never
 * contains the `$` — the prefix is rendered as a non-editable sibling
 * element so future multi-currency support can swap the symbol without
 * touching every input. The parser still tolerates `$` for paste-safety.
 */

const EMPTY_PLACEHOLDER = '—'

/**
 * Parse user-typed currency text into a Number (or null if empty / invalid).
 *
 * Accepts: digits, commas, decimal point, dollar signs, optional leading
 * minus. Strips all other characters before float coercion. Keeps at most
 * one decimal point (everything after the second `.` is discarded).
 *
 * Rejects: scientific notation (the `e` is stripped, so `1e3` parses as 13),
 * the empty/all-whitespace string, and strings that contain no digits at all.
 *
 * Examples:
 *   "$1,234.56" → 1234.56
 *   "5"         → 5
 *   "$5"        → 5
 *   "-$10.50"   → -10.50
 *   ""          → null
 *   "abc"       → null
 *   "."         → null   (no digits)
 *   "$1,234.56.78" → 1234.56   (one decimal max)
 */
export function parseCurrencyInput(raw) {
  if (raw === null || raw === undefined) return null
  const str = String(raw).trim()
  if (str === '') return null

  // Detect a leading minus (after stripping optional `$` / whitespace) so we
  // can preserve sign while stripping all non-digit / non-decimal chars.
  // We allow ONE leading minus and ignore any other minus chars in the body.
  const negative = /^-|^\$-|^-\$/.test(str)

  // Strip everything except digits and decimal points.
  let cleaned = str.replace(/[^\d.]/g, '')

  // Keep one decimal point max. Per spec, "$1,234.56.78" → 1234.56 (the
  // second decimal point TRUNCATES — we discard everything from the
  // second `.` onward, NOT just the dot itself).
  const firstDot = cleaned.indexOf('.')
  if (firstDot !== -1) {
    const secondDot = cleaned.indexOf('.', firstDot + 1)
    if (secondDot !== -1) {
      cleaned = cleaned.slice(0, secondDot)
    }
  }

  // Reject if there are no digits at all (e.g. "$", ".", "-", "abc").
  if (!/\d/.test(cleaned)) return null

  const n = parseFloat(cleaned)
  if (!Number.isFinite(n)) return null
  return negative ? -n : n
}

/**
 * Format a Number as USD currency for display. Returns the empty-state
 * placeholder ("—") when value is null / undefined / NaN. Includes the
 * `$` sign, thousands separators, and exactly 2 decimal places.
 *
 * Examples:
 *   12.345 → "$12.35"   (rounded)
 *   1234.5 → "$1,234.50"
 *   0      → "$0.00"
 *   null   → "—"
 *   NaN    → "—"
 */
export function formatCurrency(value) {
  if (value === null || value === undefined) return EMPTY_PLACEHOLDER
  const n = Number(value)
  if (!Number.isFinite(n)) return EMPTY_PLACEHOLDER
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Format a Number as bare currency (no `$` sign) for use in table cells
 * where the column header carries the sign, or in input fields where the
 * `$` is rendered as a non-editable sibling element.
 *
 * Same behavior as formatCurrency() but without the leading `$`.
 *
 * Examples:
 *   12.345 → "12.35"
 *   1234.5 → "1,234.50"
 *   0      → "0.00"
 *   null   → "—"
 */
export function formatCurrencyNumber(value) {
  if (value === null || value === undefined) return EMPTY_PLACEHOLDER
  const n = Number(value)
  if (!Number.isFinite(n)) return EMPTY_PLACEHOLDER
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export const CURRENCY_EMPTY_PLACEHOLDER = EMPTY_PLACEHOLDER

/**
 * Bare currency (no `$` sign), with the "—" empty placeholder coerced to
 * "0.00" -- for markup that concatenates its own literal `$` prefix
 * (`${{ salesPriceFormatNumber(x) }}`) and expects a number-like string
 * rather than an em-dash. Single source of truth for that fallback: both
 * `src/mixins/helpers.js`'s `salesPriceFormat` (Options API mixin method,
 * legacy callers) and any plain (non-mixin) component that needs the same
 * "$0.00, never a dash" behavior should import this rather than
 * re-implementing the `formatCurrencyNumber(...) === '—' ? '0.00' : ...`
 * fallback a second time.
 *
 * Examples:
 *   12.345 → "12.35"
 *   0      → "0.00"
 *   null   → "0.00" (NOT "—" -- see above)
 */
export function salesPriceFormatNumber(value) {
  const formatted = formatCurrencyNumber(value)
  return formatted === EMPTY_PLACEHOLDER ? '0.00' : formatted
}
